import React from 'react';
import { TrendingUp, BarChart2, Bell, Activity, X, PackageSearch } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { WidgetType } from '../../store/stockDashboardStore';

interface PickerItem {
  type: WidgetType;
  label: string;
  desc: string;
  Icon: LucideIcon;
  color: string;
}

const WIDGET_TYPES: PickerItem[] = [
  { type: 'kpi',      label: 'KPI Card',       desc: 'Key metric stat',    Icon: TrendingUp, color: '#3b82f6' },
  { type: 'category', label: 'By Category',    desc: 'Category bars',      Icon: BarChart2,  color: '#10b981' },
  { type: 'alerts',   label: 'Alerts',         desc: 'Stock alerts',       Icon: Bell,       color: '#f59e0b' },
  { type: 'activity', label: 'Activity',       desc: 'Recent log',         Icon: Activity,   color: '#8b5cf6' },
  { type: 'stock_by_item', label: 'Item Inventory Detail', desc: 'Item stock detail', Icon: PackageSearch, color: '#0ea5e9' },
  { type: 'stock_by_item_counts', label: 'Stock by Item', desc: 'New / used counts', Icon: PackageSearch, color: '#3b82f6' },
];

interface AddWidgetPickerProps {
  isDark: boolean;
  onAdd: (type: WidgetType) => void;
  onClose: () => void;
}

const AddWidgetPicker: React.FC<AddWidgetPickerProps> = ({ isDark, onAdd, onClose }) => {
  const bg     = isDark ? '#20242b' : '#ffffff';
  const border = isDark ? '#343a43' : '#e5e7eb';
  const text   = isDark ? '#f3f4f6' : '#111827';
  const muted  = isDark ? '#9ca3af' : '#6b7280';
  const hoverBg = isDark ? '#2b3140' : '#f9fafb';

  return (
    <div
      className="absolute bottom-14 left-1/2 -translate-x-1/2 rounded-xl shadow-2xl z-30"
      style={{ backgroundColor: bg, border: `1px solid ${border}` }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between px-4 pt-3 pb-2"
        style={{ borderBottom: `1px solid ${border}` }}
      >
        <span className="text-sm font-semibold" style={{ color: text }}>Add Widget</span>
        <button
          onClick={onClose}
          className="p-0.5 rounded hover:opacity-70 transition-opacity"
          style={{ color: muted }}
        >
          <X size={14} />
        </button>
      </div>

      {/* Grid of widget types */}
      <div className="flex gap-1 p-3">
        {WIDGET_TYPES.map(item => (
          <button
            key={item.type}
            onClick={() => { onAdd(item.type); onClose(); }}
            className="flex flex-col items-center gap-2 px-4 py-3 rounded-lg transition-colors"
            style={{ color: text }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = hoverBg)}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
          >
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: `${item.color}22`, color: item.color }}
            >
              <item.Icon size={20} />
            </div>
            <div className="text-center">
              <div className="text-xs font-medium whitespace-nowrap">{item.label}</div>
              <div className="text-[10px] whitespace-nowrap" style={{ color: muted }}>{item.desc}</div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

export default AddWidgetPicker;
